// src/context/PermissionContext.js
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import permissionService from '../services/permissionService';
import companyService from '../services/companyService';
import authService from '../services/authService';
import { useLanguage } from './LanguageContext';

const getRoleFromToken = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const decoded = JSON.parse(atob(token.split('.')[1]));
    return decoded?.role || null;
  } catch {
    return null;
  }
};

const PermissionContext = createContext({
  permissions: [],
  companyModules: [],
  companySettings: {},
  tenant: null,        // { companyId, name, logoUrl } — resolved from domain before login
  role: null,
  user: null,          // { username, firstName, lastName }
  pinnedQuickActions: [],
  setPinnedQuickActions: () => {},
  loading: true,
  hasModule: () => false,
  refresh: () => {},
});

export function PermissionProvider({ children }) {
  const [role, setRole] = useState(() => getRoleFromToken());
  const [permissions, setPermissions] = useState([]);
  const [companyModules, setCompanyModules] = useState([]);
  const [companyAllGranted, setCompanyAllGranted] = useState(true); // safe default
  const [companySettings, setCompanySettings] = useState({});
  const [tenant, setTenant] = useState(null); // pre-login tenant resolved from domain
  const [user, setUser] = useState(null);
  const [pinnedQuickActions, setPinnedQuickActionsState] = useState([]);
  const [loading, setLoading] = useState(true);
  const retryRef = useRef(0);
  const loadRef = useRef(null);
  const { syncFromServer } = useLanguage();

  // Resolve tenant from hostname on app load — runs once, no auth needed.
  // Result is used by Login.js to pass companyId with credentials.
  useEffect(() => {
    companyService.getByDomain(window.location.hostname)
      .then(data => setTenant(data))
      .catch(() => setTenant(null)); // unknown domain = superadmin or dev
  }, []);

  const load = useCallback(async () => {
    if (!authService.isTokenValid()) {
      setPermissions([]);
      setCompanyModules([]);
      setCompanyAllGranted(true);
      setRole(null);
      setUser(null);
      setPinnedQuickActionsState([]);
      setLoading(false);
      return;
    }

    const tokenRole = getRoleFromToken();
    if (tokenRole) setRole(tokenRole);

    try {
      if (tokenRole === 'superadmin') {
        // Super-admin has no companyId — skip tenant-scoped API calls
        const userData = await permissionService.getMyPermissions();
        setRole(userData.role || tokenRole);
        setPermissions([]);
        setCompanyModules([]);
        setCompanyAllGranted(true);
        setCompanySettings(null);
        setUser(userData.user || null);
        setPinnedQuickActionsState(userData.pinnedQuickActions || []);
        if (userData.preferredLanguage) syncFromServer(userData.preferredLanguage);
      } else if (tokenRole === 'event_manager') {
        // Confined third-party role: the API only serves /api/events and /api/users/me/*, so don't
        // request company-wide data (modules, company record) it isn't allowed to see.
        const userData = await permissionService.getMyPermissions();
        setPermissions(userData.permissions || []);
        setRole(userData.role || tokenRole);
        setCompanyModules([]);
        setCompanyAllGranted(true);
        setCompanySettings({});
        setUser(userData.user || null);
        setPinnedQuickActionsState(userData.pinnedQuickActions || []);
        if (userData.preferredLanguage) syncFromServer(userData.preferredLanguage);
      } else {
        const [userData, companyData, companies] = await Promise.all([
          permissionService.getMyPermissions(),
          permissionService.getCompanyModules(),
          companyService.getCompanies(),
        ]);
        setPermissions(userData.permissions || []);
        setRole(userData.role || tokenRole);
        setCompanyModules(companyData.modules || []);
        setCompanyAllGranted(companyData.allGranted ?? true);
        setCompanySettings(companies[0] || {});
        setUser(userData.user || null);
        setPinnedQuickActionsState(userData.pinnedQuickActions || []);
        if (userData.preferredLanguage) syncFromServer(userData.preferredLanguage);
      }
      retryRef.current = 0;
    } catch (err) {
      console.error('Failed to load permissions:', err);
      if (retryRef.current < 1) {
        retryRef.current += 1;
        setTimeout(() => loadRef.current?.(), 1500);
      } else {
        setPermissions([]);
      }
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the ref in sync
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  // Initial load
  useEffect(() => {
    load();
  }, [load]);

  // Re-load on auth changes (login / logout)
  useEffect(() => {
    const handler = () => {
      retryRef.current = 0;
      setPermissions([]);
      setCompanyModules([]);
      setCompanyAllGranted(true);
      setCompanySettings({});
      setRole(getRoleFromToken());
      setUser(null);
      setPinnedQuickActionsState([]);
      setLoading(true);
      setTimeout(() => loadRef.current?.(), 50);
    };
    window.addEventListener('authChanged', handler);
    return () => window.removeEventListener('authChanged', handler);
  }, []);

  // Two-level check:
  //   Level 1 — company subscription: does this tenant have this module?
  //   Level 2 — user permission: has the admin granted this user the module?
  const hasModule = useCallback((moduleKey) => {
    if (role === 'superadmin') return true;
    // Avoid a premature block during load — except for the confined third-party role, which must
    // not fire module-gated background requests (they would 403) before its permissions arrive.
    if (loading) return role !== 'event_manager';
    // For admin: backend already scopes permissions to company-assigned modules
    if (role === 'admin') return permissions.includes(moduleKey);
    // For other roles: two-level check (company subscription + user grant)
    if (!companyAllGranted && !companyModules.includes(moduleKey)) return false;
    return permissions.includes(moduleKey);
  }, [permissions, companyModules, companyAllGranted, role, loading]);

  // Optimistic update + fire-and-forget persist; UI shouldn't wait on the network
  // for something as low-stakes as a topbar shortcut list.
  const setPinnedQuickActions = useCallback((ids) => {
    setPinnedQuickActionsState(ids);
    permissionService.saveMyPinnedQuickActions(ids).catch(err => {
      console.error('Failed to save pinned quick actions:', err);
    });
  }, []);

  return (
    <PermissionContext.Provider value={{ permissions, companyModules, companySettings, tenant, role, user, pinnedQuickActions, setPinnedQuickActions, loading, hasModule, refresh: load }}>
      {children}
    </PermissionContext.Provider>
  );
}

export const usePermissions = () => useContext(PermissionContext);
